import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { type GradeRecord } from './GradeImporterView';
import {
  FIXED_CSV_COLS,
  TEMPLATE_HEADERS,
  GRADE_TEMPLATE_SAMPLE_ROW,
  validateGradeHeaders,
} from './gradeImporterUtils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ParsedRow {
  id: string;
  rowNum: number;
  name: string;
  material: string;
  locationName: string;
  blend: string;
  inventory: number | null;
  specificGravity: number | null;
  qualities: Record<string, number>;
  errors: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImport: (grades: GradeRecord[]) => Promise<void>;
}

const PREVIEW_COLS = [
  'name',
  'material',
  'locationName',
  'blend',
  'inventory',
  'specificGravity',
  'qualities',
];

const downloadTemplate = () => {
  const sampleValues = TEMPLATE_HEADERS.map((h) => {
    const v = GRADE_TEMPLATE_SAMPLE_ROW[h] ?? '';
    return v.includes(',') ? `"${v}"` : v;
  });
  const csv = [TEMPLATE_HEADERS.join(','), sampleValues.join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'grade_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const downloadErrors = (errorRows: ParsedRow[]) => {
  const headers = [...TEMPLATE_HEADERS, 'errors'];
  const rows = errorRows.map((r) => {
    const qualStr = JSON.stringify(r.qualities);
    return [
      r.name,
      r.material,
      r.locationName,
      r.blend,
      r.inventory ?? '',
      r.specificGravity ?? '',
      qualStr,
      `"${r.errors.join('; ')}"`,
    ].join(',');
  });
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'grade_import_errors.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const parseOptionalNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  return isNaN(n) ? null : n;
};

const validateRow = (
  raw: Record<string, unknown>,
  rowNum: number
): ParsedRow => {
  const errs: string[] = [];

  // Normalize keys to uppercase for lookup
  const upperRow: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    upperRow[k.trim().toUpperCase()] = String(v ?? '').trim();
  }

  const name = upperRow.NAME ?? '';
  const material = upperRow.MATERIAL ?? '';
  const locationName = upperRow.LOCATION ?? '';
  const blend = upperRow.BLEND ?? '';
  const inventoryRaw = upperRow.INVENTORY ?? '';
  const specificGravityRaw = upperRow.SPECIFIC_GRAVITY ?? '';

  if (!name) errs.push('NAME is required');
  if (!material) errs.push('MATERIAL is required');
  if (!locationName) errs.push('LOCATION is required');
  if (!inventoryRaw) {
    errs.push('INVENTORY is required');
  } else if (isNaN(parseFloat(inventoryRaw))) {
    errs.push('INVENTORY must be a number');
  }
  if (specificGravityRaw && isNaN(parseFloat(specificGravityRaw))) {
    errs.push('SPECIFIC_GRAVITY must be a number');
  }

  // All non-fixed columns → qualities (lowercase key)
  const qualities: Record<string, number> = {};
  for (const [upperKey, val] of Object.entries(upperRow)) {
    if (!FIXED_CSV_COLS.has(upperKey)) {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        qualities[upperKey.toLowerCase()] = num;
      }
    }
  }

  return {
    id: `row-${rowNum}`,
    rowNum,
    name,
    material,
    locationName,
    blend,
    inventory: parseOptionalNumber(inventoryRaw),
    specificGravity: parseOptionalNumber(specificGravityRaw),
    qualities,
    errors: errs,
  };
};

export const ImportGradeModal = ({
  open,
  onClose,
  onImport,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headerError, setHeaderError] = useState<string[] | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) {
      setParsedRows([]);
      setSelectedIds(new Set());
      setFileName('');
      setHeaderError(null);
    }
  }, [open]);

  const validRows = parsedRows.filter((r) => r.errors.length === 0);
  const errorRows = parsedRows.filter((r) => r.errors.length > 0);

  const applyRows = (rows: ParsedRow[]) => {
    setParsedRows(rows);
    setSelectedIds(
      new Set(rows.filter((r) => r.errors.length === 0).map((r) => r.id))
    );
  };

  const processFile = useCallback((file: File) => {
    setFileName(file.name);
    setHeaderError(null);
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'csv') {
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const fileHeaders = result.meta.fields ?? [];
          const { valid, missing } = validateGradeHeaders(fileHeaders);
          if (!valid) {
            setHeaderError(missing);
            return;
          }
          applyRows(result.data.map((row, i) => validateRow(row, i + 2)));
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target?.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: '',
        });
        const fileHeaders = json.length > 0 ? Object.keys(json[0]) : [];
        const { valid, missing } = validateGradeHeaders(fileHeaders);
        if (!valid) {
          setHeaderError(missing);
          return;
        }
        applyRows(json.map((row, i) => validateRow(row, i + 2)));
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(validRows.map((r) => r.id)) : new Set());
  };

  const handleImport = async () => {
    const toImport: GradeRecord[] = validRows
      .filter((r) => selectedIds.has(r.id))
      .map((r) => ({
        id: crypto.randomUUID(),
        name: r.name,
        materialCategory: r.material,
        locationName: r.locationName,
        locationId: null,
        blend: r.blend,
        dump: '',
        active: true,
        gradeCategory: null,
        specification: '',
        inventory: r.inventory,
        specificGravity: r.specificGravity,
        qualities: r.qualities,
      }));
    setImporting(true);
    try {
      await onImport(toImport);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsedRows([]);
    setSelectedIds(new Set());
    setFileName('');
    setHeaderError(null);
    onClose();
  };

  const handleRetry = () => {
    setParsedRows([]);
    setHeaderError(null);
    setFileName('');
  };

  const allSelected =
    validRows.length > 0 && selectedIds.size === validRows.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const previewColLabel = (col: string): string => {
    const map: Record<string, string> = {
      name: t('grade_importer_col_name'),
      material: t('grade_importer_col_material'),
      locationName: t('grade_importer_col_location_name'),
      blend: t('grade_importer_field_blend'),
      inventory: t('grade_importer_col_inventory'),
      specificGravity: t('grade_importer_col_specific_gravity'),
      qualities: t('grade_importer_col_qualities'),
    };
    return map[col] ?? col;
  };

  const fmtPreviewCell = (col: string, row: ParsedRow): string => {
    if (col === 'qualities') {
      const entries = Object.entries(row.qualities);
      return entries.length > 0
        ? entries.map(([k, v]) => `${k.toUpperCase()}:${v}`).join(' ')
        : '—';
    }
    const val = row[col as keyof ParsedRow];
    if (val === null || val === undefined || val === '') return '—';
    return String(val);
  };

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={parsedRows.length > 0 ? 'lg' : headerError ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t: { palette: { primary: { main: string } } }) =>
            alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('grade_importer_import_title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            variant="outlined"
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}
          >
            {t('grade_importer_download_template')}
          </Button>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {headerError ? (
          /* ── Header mismatch error ── */
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.error.main, 0.35),
              bgcolor: (t) => alpha(t.palette.error.main, 0.04),
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.error.main, 0.3),
              }}
            >
              <Typography sx={{ fontSize: 26 }}>⚠️</Typography>
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="error.main"
              gutterBottom
            >
              {t('grade_importer_invalid_format')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('grade_importer_invalid_format_body')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                justifyContent: 'center',
                mb: 3,
              }}
            >
              {headerError.map((col) => (
                <Box
                  key={col}
                  sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: '6px',
                    bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.error.main, 0.3),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                  >
                    {col}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('grade_importer_download_template')}
              </Button>
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('grade_importer_try_again')}
              </Button>
            </Box>
          </Box>
        ) : parsedRows.length === 0 ? (
          /* ── Drop zone ── */
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => {
              document.getElementById('grade-file-input')?.click();
            }}
            sx={{
              border: '2px dashed',
              borderColor: (t) =>
                isDragging
                  ? t.palette.primary.main
                  : alpha(t.palette.primary.main, 0.35),
              borderRadius: 3,
              py: 6,
              px: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              bgcolor: (t) =>
                isDragging
                  ? alpha(t.palette.primary.main, 0.08)
                  : alpha(t.palette.primary.main, 0.03),
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              },
            }}
          >
            <input
              id="grade-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                border: '2px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                boxShadow: (t) =>
                  `0 0 32px ${alpha(t.palette.primary.main, 0.18)}`,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 34, color: 'primary.main' }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              gutterBottom
            >
              {t('grade_importer_drop_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {t('grade_importer_drop_body')}
            </Typography>
            <Box
              sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
            >
              {['.CSV', '.XLSX', '.XLS'].map((fmt) => (
                <Box
                  key={fmt}
                  sx={{
                    px: 1.5,
                    py: 0.35,
                    borderRadius: '6px',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.primary.main, 0.22),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {fmt}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                sx={{ borderRadius: '8px', px: 3, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('grade-file-input')?.click();
                }}
              >
                {t('grade_importer_browse_file')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: '8px', px: 3, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadTemplate();
                }}
              >
                {t('grade_importer_download_template')}
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Results ── */
          <Box>
            {/* File info bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.divider, 0.5),
              }}
            >
              <InsertDriveFileOutlinedIcon
                fontSize="small"
                sx={{ color: 'text.secondary' }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
                noWrap
              >
                {fileName}
              </Typography>
              <Chip
                label={`${validRows.length} ${t('grade_importer_valid')}`}
                color="success"
                size="small"
              />
              {errorRows.length > 0 && (
                <Chip
                  label={`${errorRows.length} error${
                    errorRows.length > 1 ? 's' : ''
                  }`}
                  color="error"
                  size="small"
                />
              )}
              <Button
                size="small"
                sx={{
                  color: 'text.secondary',
                  ml: 0.5,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                }}
                onClick={() => {
                  setParsedRows([]);
                  setFileName('');
                }}
              >
                {t('grade_importer_change')}
              </Button>
            </Box>

            {/* Error rows */}
            {errorRows.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {t('grade_importer_error_rows_label', {
                      count: errorRows.length,
                    })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      downloadErrors(errorRows);
                    }}
                    variant="outlined"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      color: 'error.main',
                      borderColor: (t) => alpha(t.palette.error.main, 0.4),
                      '&:hover': {
                        bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                      },
                    }}
                  >
                    {t('grade_importer_download_errors')}
                  </Button>
                </Box>
                <TableContainer
                  sx={{
                    maxHeight: 200,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.error.main, 0.3),
                    bgcolor: (t) => alpha(t.palette.error.main, 0.02),
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          t('grade_importer_col_row'),
                          ...PREVIEW_COLS.map(previewColLabel),
                          t('grade_importer_col_errors'),
                        ].map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: 'background.paper',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              color: 'error.main',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                            }}
                          >
                            {row.rowNum}
                          </TableCell>
                          {PREVIEW_COLS.map((col) => (
                            <TableCell
                              key={col}
                              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              {fmtPreviewCell(col, row)}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              color: 'error.main',
                              maxWidth: 200,
                            }}
                          >
                            {row.errors.join(' · ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Valid rows */}
            {validRows.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {t('grade_importer_valid_rows_label', {
                    selected: selectedIds.size,
                    total: validRows.length,
                  })}
                </Typography>
                <TableContainer
                  sx={{
                    maxHeight: 320,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.divider, 0.6),
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                              toggleAll(e.target.checked);
                            }}
                            size="small"
                          />
                        </TableCell>
                        {PREVIEW_COLS.map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: 'background.paper',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {previewColLabel(col)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validRows.map((row) => (
                        <TableRow
                          key={row.id}
                          selected={selectedIds.has(row.id)}
                          hover
                          onClick={() => {
                            toggleRow(row.id);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedIds.has(row.id)}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={() => {
                                toggleRow(row.id);
                              }}
                            />
                          </TableCell>
                          {PREVIEW_COLS.map((col) => (
                            <TableCell
                              key={col}
                              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              {fmtPreviewCell(col, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {parsedRows.length > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              void handleImport();
            }}
            disabled={selectedIds.size === 0 || importing}
            startIcon={
              importing ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('grade_importer_import_selected', { count: selectedIds.size })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
